import {
  Circle,
  CircleDot,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Copy,
  AlertOctagon,
  GitMerge,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import type { AlertStatus } from '../../types'

const CONFIG: Record<
  AlertStatus,
  { Icon: React.ComponentType<{ size?: number; className?: string }>; label: string; color: string }
> = {
  open: { Icon: Circle, label: 'Open', color: 'text-status-info' },
  in_review: { Icon: CircleDot, label: 'In review', color: 'text-status-pending' },
  escalated: { Icon: ArrowUpRight, label: 'Escalated', color: 'text-priority-critical' },
  merged: { Icon: GitMerge, label: 'Merged into case', color: 'text-muted-fg' },
  // The backend's uiAlertStatus collapses every closed_* + merged
  // down to a single wire value "closed"; this entry is the one
  // real production renders hit. Granular icons below stay for
  // mock fixtures + any future un-collapsed callers.
  closed: { Icon: XCircle, label: 'Closed', color: 'text-muted-fg' },
  closed_fraud: { Icon: AlertOctagon, label: 'Closed — fraud confirmed', color: 'text-priority-critical' },
  closed_false_positive: { Icon: CheckCircle2, label: 'Closed — false positive', color: 'text-status-success' },
  closed_duplicate: { Icon: Copy, label: 'Closed — duplicate', color: 'text-muted-fg' },
  closed_other: { Icon: XCircle, label: 'Closed', color: 'text-muted-fg' },
}

/** Icon-only status indicator for the queue row. Tooltip carries the label. */
export function StatusIcon({ status }: { status: AlertStatus }) {
  const { Icon, label, color } = CONFIG[status]
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label={`Status: ${label}`} className={cn('inline-flex', color)}>
          <Icon size={14} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
